import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api';

export type CommitteePaperFilter = 'ALL' | 'PENDING' | 'UNDER_EVAL' | 'APPROVED' | 'REJECTED';
export type CommitteePaperStatus =
  | 'PENDING_REVIEW'
  | 'REVISION_REQUESTED'
  | 'UNDER_EVALUATION'
  | 'APPROVED_TO_PAY'
  | 'REJECTED';

export interface CommitteePaperItem {
  id: string;
  folio: string;
  title: string;
  author: {
    name: string;
    institution: string;
  };
  academicUnit: string;
  status: CommitteePaperStatus;
  registrationStatus: string;
  evaluatorsCount: number;
  evaluatorsMax: number;
  evaluationId: string | null;
  evaluationStatus: string | null;
  hasCleanVersion: boolean;
  documentStatus?: 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN' | null;
  latestCleanVersionNumber?: number | null;
  latestRevisionVersionNumber?: number | null;
  canStartSecondRound?: boolean;
  secondRoundStartReason?: string | null;
  requiresCleanForAssignment?: boolean;
  canAssignEvaluators?: boolean;
  assignDisabledReason?: string | null;
  document?: {
    status?: 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN' | null;
    hasCleanVersion?: boolean;
    latestCleanVersionNumber?: number | null;
  } | null;
  assignmentPolicy?: {
    requireCleanPaper?: boolean;
    canAssign?: boolean;
    reason?: string | null;
  } | null;
  evaluators: Array<{
    id: string;
    name: string;
    email: string;
    hasDecision: boolean;
  }>;
  createdAt: string;
}

export type CommitteePaperVersionType = 'ORIGINAL' | 'CLEAN' | 'REVISION';

export interface CommitteePaperVersionItem {
  id: string;
  versionNumber: number;
  versionType: CommitteePaperVersionType;
  roundNumber: number | null;
  archivoNombre: string | null;
  archivoMime: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  isActive: boolean;
  isLegacyOriginal?: boolean;
  downloadUrl: string;
}

export interface CommitteeEvaluatorAvailableItem {
  id: string;
  memberId: string;
  name: string;
  email: string;
  institution: string | null;
  currentLoad: number;
  isAtMaxLoad: boolean;
  status: 'ACTIVE';
  selected: boolean;
}

export interface ReassignImpactPreview {
  inProgressCount: number;
  currentNames: string[];
  newNames: string[];
  requiresConfirmation: boolean;
}

interface PapersQueryParams {
  eventId: string;
  filter: CommitteePaperFilter;
  search: string;
}

async function fetchCommitteePapers(params: PapersQueryParams): Promise<CommitteePaperItem[]> {
  const { data } = await api.get('/committee/papers', {
    params: {
      eventId: params.eventId,
      filter: params.filter,
      search: params.search || undefined,
    },
  });

  if (!Array.isArray(data?.papers)) {
    return [];
  }

  return data.papers as CommitteePaperItem[];
}

async function fetchPaperVersions(paperId: string): Promise<CommitteePaperVersionItem[]> {
  const { data } = await api.get(`/committee/papers/${paperId}/versions`);
  if (!Array.isArray(data?.versions)) {
    return [];
  }
  return data.versions as CommitteePaperVersionItem[];
}

async function fetchAvailableEvaluators(eventId: string, paperId: string): Promise<CommitteeEvaluatorAvailableItem[]> {
  const { data } = await api.get('/committee/evaluators/available', {
    params: { eventId, paperId },
  });
  if (!Array.isArray(data?.evaluators)) {
    return [];
  }
  return data.evaluators as CommitteeEvaluatorAvailableItem[];
}

export function useCommitteePapers(eventId: string, filter: CommitteePaperFilter, search: string) {
  return useQuery({
    queryKey: ['committee-papers', eventId, filter, search],
    queryFn: () => fetchCommitteePapers({ eventId, filter, search }),
    enabled: Boolean(eventId),
    staleTime: 30_000,
  });
}

export function useAvailableEvaluators(eventId: string, paperId: string, enabled = true) {
  return useQuery({
    queryKey: ['committee-evaluators-available', eventId, paperId],
    queryFn: () => fetchAvailableEvaluators(eventId, paperId),
    enabled: Boolean(eventId && paperId && enabled),
    staleTime: 30_000,
  });
}

export function useCommitteePaperVersions(paperId: string, enabled = true) {
  return useQuery({
    queryKey: ['committee-paper-versions', paperId],
    queryFn: () => fetchPaperVersions(paperId),
    enabled: Boolean(paperId && enabled),
    staleTime: 10_000,
  });
}

export function useUploadCleanVersion(_eventId: string, paperId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/committee/papers/${paperId}/upload-clean`, formData);
      return data;
    },
    onSuccess: async (data) => {
      const uploadedVersionNumber =
        typeof data?.version?.versionNumber === 'number' ? data.version.versionNumber : null;

      queryClient.setQueriesData<CommitteePaperItem[]>({ queryKey: ['committee-papers'] }, (current) => {
        if (!Array.isArray(current)) return current;

        return current.map((paper) => {
          if (paper.id !== paperId) return paper;

          return {
            ...paper,
            documentStatus: 'CLEAN_AVAILABLE',
            hasCleanVersion: true,
            latestCleanVersionNumber: uploadedVersionNumber ?? paper.latestCleanVersionNumber ?? null,
            document: {
              ...(paper.document || {}),
              status: 'CLEAN_AVAILABLE',
              hasCleanVersion: true,
              latestCleanVersionNumber: uploadedVersionNumber ?? paper.document?.latestCleanVersionNumber ?? null,
            },
          };
        });
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['committee-paper-versions', paperId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-papers'] }),
        queryClient.refetchQueries({ queryKey: ['committee-papers'], type: 'active' }),
      ]);
    },
  });
}

export function useAssignEvaluators(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paperId, evaluatorIds }: { paperId: string; evaluatorIds: string[] }) =>
      api.post(`/committee/papers/${paperId}/assign`, { evaluatorIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}

export function useReassignImpact() {
  return useMutation({
    mutationFn: ({ paperId, evaluatorIds }: { paperId: string; evaluatorIds: string[] }) =>
      api.post(`/committee/papers/${paperId}/reassign/evaluate-impact`, {
        newEvaluatorIds: evaluatorIds,
      }),
  });
}

export function useReassignEvaluators(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paperId,
      evaluatorIds,
      confirmImpact,
    }: {
      paperId: string;
      evaluatorIds: string[];
      confirmImpact: boolean;
    }) =>
      api.put(`/committee/papers/${paperId}/assign`, {
        evaluatorIds,
        confirmImpact,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}
export function useStartSecondRound(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paperId }: { paperId: string }) => {
      const { data } = await api.post(`/committee/papers/${paperId}/start-round`);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}
